import React from 'react';
import { Props } from './index'; // reuse your existing Props type
import CustomHeadline from '../Global/CustomHeadline';
import CtaLink from '../Global/CtaLink';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import IconSection from './IconSection';
import * as styles from './MediaLogoSection.module.css';
import {
    getColor,
    getMultiChoice,
    getMediaData,
    optimizeKontentImageUrl,
    buildKontentSrcSet,
    imagePreset,
    getDisplayDimensions,
} from '../Global/DataUtils';
import LazyImage from '../Global/LazyImage';
import { IconSectionData } from '../../../types/KontentProps';

const MediaLogoSection: React.FC<Props> = (props) => {
    const {
        component_anchor_name,
        display_sub_headline,
        display_sub_headline_tag,
        sub_headline_style,
        display_headline,
        display_headline_tag,
        description_copy,
        description_style,
        description_tag,
        cta_show_or_hide,
        cta_configuration__cta_label,
        cta_configuration__cta_location,
        cta_configuration__cta_location_anchor_point,
        cta_configuration__cta_window_action,
        general_large___small_image_selection__large,
        general_large___small_image_selection__small,
        primary_background_color,
        primary_font_color,
        secondary_background_color,
        secondary_font_color,
        icon_section_style,
        icon_statements,
        icon_section_title,
        extend_bottom_edge_of_component,
    } = props;

    const extendBottom =
        extend_bottom_edge_of_component?.value?.[0]?.codename === 'yes'
            ? true
            : false;
    const ctaShowHide =
        getMultiChoice(cta_show_or_hide) === 'show' &&
        cta_configuration__cta_label?.value;
    const ctaWindowAction = getMultiChoice(
        cta_configuration__cta_window_action
    );
    const primaryBackgroundColor =
        getColor(primary_background_color) || '#FFFFFF';
    const primaryTextColor = getColor(primary_font_color) || '#505251';
    const secondaryBackgroundColor =
        getColor(secondary_background_color) || '#534138';
    const secondaryTextColor = getColor(secondary_font_color) || '#FFFFFF';

    const largeImageData = getMediaData(
        general_large___small_image_selection__large
    );
    const smallImageData = getMediaData(
        general_large___small_image_selection__small
    );

    const renderImages = () => {
        if (!largeImageData) return null;

        return (
            <picture data-kontent-element-codename="general_large___small_image_selection__large">
                {smallImageData && (
                    <source
                        media="(max-width: 1319px)"
                        srcSet={buildKontentSrcSet(
                            smallImageData.url,
                            imagePreset.fullWidth.mobile.w,
                            { fm: 'webp' as const, q: 95 }
                        )}
                        sizes="100vw"
                        type="image/webp"
                    />
                )}
                <LazyImage
                    src={optimizeKontentImageUrl(largeImageData.url, {
                        ...imagePreset.fullWidth.desktop,
                        q: 95,
                    })}
                    srcSet={buildKontentSrcSet(
                        largeImageData.url,
                        imagePreset.fullWidth.desktop.w,
                        { fm: 'webp' as const, q: 95 }
                    )}
                    sizes="100vw"
                    alt={largeImageData.description || largeImageData.name}
                    className={styles.pictureImg}
                    {...getDisplayDimensions(
                        largeImageData.width,
                        largeImageData.height,
                        imagePreset.fullWidth.desktop.w
                    )}
                    priority
                />
            </picture>
        );
    };

    const style = icon_section_style?.value?.[0]?.codename;

    return (
        <div
            className={styles.container}
            style={
                {
                    '--section-margin-bottom': extendBottom
                        ? '8rem'
                        : 'inherit',
                } as React.CSSProperties
            }
        >
            <div
                className={styles.columnContainer1}
                style={{
                    color: primaryTextColor,
                    backgroundColor: primaryBackgroundColor,
                }}
            >
                <div className={styles.textBlock}>
                    {component_anchor_name?.value && (
                        <div id={component_anchor_name.value} />
                    )}

                    {general_large___small_image_selection__small
                        ?.value?.[0] && (
                        <>
                            <div className={styles.mobilePictureContainer}>
                                {renderImages()}
                            </div>
                        </>
                    )}

                    <div className={styles.upperTextBlock}>
                        {display_sub_headline?.value && (
                            <div
                                className={styles.subHeadlineContainer1}
                                style={
                                    {
                                        '--media-secondary-bg':
                                            secondaryBackgroundColor,
                                        '--media-secondary-text':
                                            secondaryTextColor,
                                    } as React.CSSProperties
                                }
                            >
                                <CustomHeadline
                                    style={
                                        sub_headline_style
                                            ? sub_headline_style.value[0]
                                                  ?.codename
                                            : 'h6'
                                    }
                                    tag={
                                        display_sub_headline_tag?.value[0]
                                            ?.codename
                                            ? display_sub_headline_tag
                                            : {
                                                  value: [
                                                      {
                                                          codename: 'h2',
                                                          name: 'h2',
                                                      },
                                                  ],
                                              }
                                    }
                                    headline_value={display_sub_headline}
                                    alignment={{
                                        value: [{ codename: 'left' }],
                                    }}
                                    overrideStyles={{
                                        marginBottom: '2rem',
                                        letterSpacing: '0 !important',
                                        fontFamily: 'Barlow Regular',
                                        fontSize:
                                            sub_headline_style?.value[0]
                                                ?.codename == 'h5'
                                                ? '22px'
                                                : '',
                                    }}
                                    elementCodename="display_sub_headline"
                                />
                            </div>
                        )}
                        {display_headline?.value && (
                            <div className={styles.headlineContainer}>
                                <CustomHeadline
                                    style={'h2'}
                                    tag={
                                        display_headline_tag?.value[0]?.codename
                                            ? display_headline_tag
                                            : {
                                                  value: [
                                                      {
                                                          codename: 'h1',
                                                          name: 'h1',
                                                      },
                                                  ],
                                              }
                                    }
                                    headline_value={display_headline}
                                    enable_trademark_styling={
                                        props.enable_trademark_styling
                                    }
                                    alignment={{
                                        value: [{ codename: 'left' }],
                                    }}
                                    overrideStyles={{
                                        marginBottom: '2rem',
                                        fontSize: '45px',
                                    }}
                                    elementCodename="display_headline"
                                />
                            </div>
                        )}

                        <div
                            className={styles.subHeadlineContainer2}
                            style={
                                !description_copy?.value
                                    ? { display: 'none' }
                                    : undefined
                            }
                        >
                            <CustomHeadline
                                style={
                                    description_style
                                        ? description_style.value[0]?.codename
                                        : 'h6'
                                }
                                tag={
                                    description_tag?.value[0]?.codename
                                        ? description_tag
                                        : {
                                              value: [
                                                  {
                                                      codename: 'p',
                                                      name: 'p',
                                                  },
                                              ],
                                          }
                                }
                                headline_value={
                                    description_copy || { value: '' }
                                }
                                alignment={{
                                    value: [{ codename: 'left' }],
                                }}
                                overrideStyles={{
                                    marginBottom: '2rem',
                                    letterSpacing: '0 !important',
                                    lineHeight: '1.4',
                                    fontFamily: 'Barlow Regular',
                                    fontWeight:
                                        description_style?.value?.[0]
                                            ?.codename === 'h6'
                                            ? 400
                                            : 'auto',
                                }}
                                elementCodename="description_copy"
                            />
                        </div>

                        {ctaShowHide && (
                            <div className={styles.ctaWrapper}>
                                <CtaLink
                                    href={BuildLinkHref(
                                        cta_configuration__cta_location,
                                        cta_configuration__cta_location_anchor_point
                                    )}
                                    target={
                                        ctaWindowAction === 'new_window'
                                            ? '_blank'
                                            : undefined
                                    }
                                    visualStyle="outlineDark"
                                >
                                    {cta_configuration__cta_label?.value}
                                </CtaLink>
                            </div>
                        )}
                    </div>
                    {icon_section_style?.value?.[0]?.codename !== 'hide' &&
                        icon_statements?.value?.length > 0 && (
                            <>
                                <IconSection
                                    iconData={
                                        {
                                            icon_section_style:
                                                icon_section_style,
                                            icon_statements: icon_statements,
                                            icon_section_title:
                                                icon_section_title,
                                        } as IconSectionData
                                    }
                                    // iconSectionBackgroundColor={
                                    //     iconSectionBackgroundColor
                                    // }
                                    // iconSectionTextColor={iconSectionTextColor}
                                />
                            </>
                        )}
                </div>

                <div
                    className={styles.imageBlock}
                    style={{
                        color: secondaryTextColor || 'inherit',
                        backgroundColor: secondaryBackgroundColor || 'inherit',
                    }}
                >
                    {general_large___small_image_selection__large
                        ?.value?.[0] && (
                        <div className={styles.pictureContainer}>
                            {renderImages()}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MediaLogoSection;
